import React, { useMemo, useState, useEffect} from 'react';
import { Factory } from 'lucide-react';
import { getCatalogLookupsBulk } from '../../products/api/ProductCatalogApi';

import { SimplifiedTraceEvent } from '../models/DeliveryOptionDetailsModel';
import RoutingSection from './RoutingSection';
import CarrierSection from './CarrierSection';
import PackagingSection from './PackagingSection';
import CalendarSection from './CalendarSection';
import { TraceDataProcessor } from '../api/TraceDataProcessor';
import { TraceLoadingAnimation } from './TraceLoadingAnimation';
import { getConfig } from '../../modules/Config/Config';

// Module-level cache
let catalogLookupsCache: { data: Record<string, any> | null; timestamp: number } = {
  data: null,
  timestamp: 0,
};

const getCatalogLookupsBulkSingleton = async (): Promise<Record<string, any>> => {
  const physicalproductcataloglookupcache = await getConfig("physicalproductcataloglookupcache");
  const cacheDuration = physicalproductcataloglookupcache?.durationInMS || 60 * 60 * 1000;
  const now = Date.now();  
  if (catalogLookupsCache.data && now - catalogLookupsCache.timestamp < cacheDuration) return catalogLookupsCache.data;
  try {
    const data = await getCatalogLookupsBulk();
    catalogLookupsCache = { data, timestamp: now };
    return data;
  } catch (error) {
    console.error("Failed to fetch catalog lookups:", error);
    throw error;
  }
};

export interface DeliveryOptionsDetailsCardProps {
  debugJson: SimplifiedTraceEvent[];
}

const DeliveryOptionsDetailsCard: React.FC<DeliveryOptionsDetailsCardProps> = ({ debugJson }) => {
  const [catalogLookups, setCatalogLookups] = useState<Record<string, any> | null>(null);
  const [isLoadingCatalog, setIsLoadingCatalog] = useState(true);

  useEffect(() => {
    const fetchCatalog = async () => {
      try {
        const data = await getCatalogLookupsBulkSingleton();
        setCatalogLookups(data);
      } catch (error) {
        console.error("Error loading catalog lookups:", error);
      } finally {
        setIsLoadingCatalog(false);
      }
    };

    fetchCatalog();
  }, []);

  // useMemo now only executes logic AFTER catalogLookups is populated
  const data = useMemo(() => {
    // Check 1: Ensure we have the debug JSON
    if (!debugJson) return null;
    
    // Check 2: Wait until the lookup is finished (prevents mid-process changes)
    if (isLoadingCatalog || !catalogLookups) return null;

    // Create the processor with a static "snapshot" of the lookup data
    const processor = new TraceDataProcessor(debugJson, catalogLookups);
    return processor.process();
  }, [debugJson, catalogLookups, isLoadingCatalog]);

  // Handle the loading state so the UI doesn't try to render with empty data
  if (isLoadingCatalog) {
    return <TraceLoadingAnimation  displayText={debugJson?.length?.toString()} subTitle={`Looking up catalog data`} />                    
  }

  if (!data || Object.keys(data.sites).length === 0) return null;

    return (
    <div className="w-full text-black font-sans p-2">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {Object.entries(data.sites).map(([siteCode, siteShipments]) => (
          <div key={siteCode} className="bg-white border-2 border-black rounded-xl overflow-hidden shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] flex flex-col h-fit">
            <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', padding: '12px', backgroundColor: 'white', gap: '8px' }}>
              <Factory size={20} color="black" style={{ flexShrink: 0 }} />
              <h3 style={{ margin: 0, fontSize: '20px', textTransform: 'uppercase', letterSpacing: '-0.05em', color: 'black', lineHeight: 1 }}>{siteCode}</h3>
            </div>
            <div className="p-4 space-y-8">
              {siteShipments.map((ship, sIdx) => {
                
                const shipmentId = `${siteCode}-${sIdx}`;
                const firstTag = ship.Products[0]?.Tag;                                
                const routing = data.routingDecisions[firstTag];

                return (
                  <div key={shipmentId} className="bg-teal-50/50 border-2 border-teal-200 rounded-xl p-3 shadow-sm flex flex-col gap-4 text-black">
                    {/* CARRIERS */}
                    <CarrierSection
                      shipment={ship}
                    />
                    
                    {/* PACKAGING DETAIL */}
                    <PackagingSection
                      shipment={ship}
                      packageGeometry={ship.PackageGeometry}
                    />

                    <div className="space-y-1.5">
                    <RoutingSection 
                        ship={ship} shipmentId={shipmentId} routing={routing}
                      />           
                                            
                      <CalendarSection
                        shipment={ship}
                        orderDate={data.orderDate}
                        prodCalendar={data.prodCalendar}
                      />
                    </div>

                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
      <style>{`
        .custom-scrollbar::-webkit-scrollbar { height: 8px; width: 8px; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
        .fc-mini-compact .fc-header-toolbar { display: flex !important; justify-content: center !important; padding: 2px !important; margin-bottom: 2px !important; gap: 8px; }
        .fc-mini-compact .fc-toolbar-title { font-size: 8px !important; font-weight: 900; text-transform: uppercase; text-align: center; color: black; }
        .fc-mini-compact .fc-button { padding: 1px 3px !important; font-size: 8px !important; background: white !important; border: 1px solid black !important; color: black !important; box-shadow: 1px 1px 0px black !important; }
        .fc-mini-compact .fc-button:active { box-shadow: none !important; transform: translate(1px, 1px); }
        .fc-mini-compact .fc-col-header-cell-cushion { font-size: 7px; text-transform: uppercase; font-weight: 900; color: #1e293b; }
        .fc-mini-compact .fc-daygrid-day-frame { min-height: 70px !important; height: 70px !important; }
        .prod-work { background-color: #dcfce7 !important; color: #15803d !important; }
        .prod-closed { background-color: #fee2e2 !important; color: #b91c1c !important; }
        .good { color: #10b981 !important; }
        .error { color: #ef4444 !important; }
        .fc-day-other { opacity: 0.15; }
      `}</style>
    </div>
  );
};

export default DeliveryOptionsDetailsCard;